import React, { useEffect, useState } from 'react';
import { InputNumber, Card, Button, Tooltip } from 'antd';
import { CheckOutlined, TagFilled, InfoCircleOutlined } from '@ant-design/icons';
import './FeatureComparisonTable.scss';
import * as ENVIRONMENT from '../../../../env.js'
import posthog from "posthog-js";
import { features } from '../../data/data.js';

const utm_source = localStorage.getItem("utm_source") || "";
const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
];

const FeatureComparisonTable = ({ selectedPlan, annual, setSelectedPlan }) => {
    const subscriptions = [
        {
            for: "For anyone",
            name: "Free Trial",
            type: "free_trial",
            package_type: "free_trial_ai",
            desc: "12 free credits",
            price: "$0",
            price_monthly: "$0",
            price_time: "",
            price_details: "14-day limited trial",
            price_details_monthly: "14-day limited trial",
            discount_statement: "",
            discount_statement_monthly: "",
            switch_to_subscription: "",
            button_text: "Start your free trial",
            credits: "",
            credits_monthly: "",

        },
        {
            for: "For new sellers",
            name: "Starter",
            type: "subscription",
            package_type: "starter_ai",
            desc: "32 credits a month. Approx. 8 products a month",
            price: "$304",
            price_monthly: "$352",
            price_time: "/month",
            price_details: "Billed annually. $38 per product",
            price_details_monthly: "Billed monthly. $44 per product",
            discount_statement: "22% discount on bundle price",
            discount_statement_monthly: "14% off the bundle price",
            switch_to_subscription: "",
            button_text: "Sign up for Starter",
            credits: "All 384 credits available at once. 4 credits per product.  Unlimited image generations.",
            credits_monthly: "32 credits available monthly. 4 credits per product. Unlimited image generations.",
        },
        {
            for: "For growing sellers",
            name: "Growth",
            type: "subscription",
            package_type: "growth_ai",
            desc: "64 credits a month. Approx. 16 products a month",
            price: "$576",
            price_monthly: "$680",
            price_time: "/month",
            price_details: "Billed annually. $36 per product",
            price_details_monthly: "Billed monthly. $42.5 per product",
            discount_statement: "26% discount on bundle price",
            discount_statement_monthly: "14% off the bundle price",
            switch_to_subscription: "",
            button_text: "Sign up for Growth",
            credits: "All 768 credits available at once. 4 credits per product.  Unlimited image generations.",
            credits_monthly: "64 credits available monthly. 4 credits per product. Unlimited image generations.",
        },
        {
            for: "For pro sellers",
            name: "Pro",
            type: "subscription",
            package_type: "pro_ai",
            desc: "256 credits a month. Approx. 64 products a month",
            credits: "12 credits per image. All 180 credits available at once.",
            price: "$2,176",
            price_monthly: "$2,624",
            price_time: "/month",
            price_details: "Billed annually. $34 per product",
            price_details_monthly: "Billed monthly. $41 per product",
            discount_statement: "30% discount on bundle price",
            discount_statement_monthly: "14% off the bundle price",
            switch_to_subscription: "",
            button_text: "Sign up for Pro",
            credits: "All 3072 credits available at once. 4 credits per product.  Unlimited image generations.",
            credits_monthly: "256 credits available monthly. 4 credits per product. Unlimited image generations.",
        },
        {
            for: "For large teams",
            name: "Enterprise",
            type: "subscription",
            package_type: "enterprise",
            desc: "Custom plan with 3D/AR",
            credits: "Contact us",
            price: "Let's talk",
            price_monthly: "Let's talk",
            price_details: "Schedule a time to discuss",
            price_details_monthly: "Schedule a time to discuss",
            discount_statement: "",
            discount_statement_monthly: "",
            switch_to_subscription: "",
            button_text: "Schedule a demo",
            credits: <a href="/contact-us" className='manrope f-12 w-500 lh-20 blue'>Contact us</a>,
            credits_monthly: <a href="/contact-us" className='manrope f-12 w-500 lh-20 blue'>Contact us</a>,
        }
    ];

    const basic_features_subscriptions = [
        {
            feature: "Product uploads",
            free: "3",
            starter: "8/month or 96/annually",
            growth: "16/month or 192/annually",
            pro: "64/month or 768/annually",
            enterprise: "Custom"
        },
        {
            feature: "Image generations",
            free: "3 per product",
            starter: "Unlimited",
            growth: "Unlimited",
            pro: "Unlimited",
            enterprise: "Unlimited"
        },
        {
            feature: "Final 2K or 4K images (no watermark)",
            free: "4 per product",
            starter: "4 per product",
            growth: "4 per product",
            pro: "4 per product",
            enterprise: "4 per product"
        },
        {
            feature:"Video generation from final image (720p)",
            free: "2 credits each",
            starter: "2 credits each",
            growth: "2 credits each",
            pro: "2 credits each",
            enterprise: "2 credits each"
        },
        {
            feature:"Multiple aspect ratios",
            free:"Square, Landscape, Portrait",
            starter:"Square, Landscape, Portrait",
            growth:"Square, Landscape, Portrait",
            pro:"Square, Landscape, Portrait",
            enterprise:"Square, Landscape, Portrait"
        },
        {
            feature: "User licenses",
            free: "3",
            starter: "3",
            growth: "6",
            pro: "Unlimited",
            enterprise: "Unlimited"
        },
    ];

    const plus_features_subscriptions = [
           {
            feature:"Editing",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature: "Resize product",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature: "Magic erase",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature: "Replace items",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature:"Color correction",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature:"Texture transfer (small items, logos etc)",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature: "Upload inspiration",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature: "Upload props",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature:"Bulk uploads",
            free: false,
            starter: false,
            growth: false,
            pro: true,
            enterprise: true
        },
        {
            feature:"Brand guidelines support",
            tooltip: "All generations and edits based on brand aesthetic, lighting, room types, props and camera angles",
            free: false,
            starter: false,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature: "Review Gallery (for quality managemet)",
            free: false,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature:"Team collaboration",
            free: false,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature:"User Management",
            free: false,
            starter: true,
            growth: true,
            pro: true,  
            enterprise: true
        },
        {
            feature: "Asset management",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
        {
            feature:"Maximum concurrent image generations",
            free: "1",
            starter: "3",
            growth: "9",
            pro: "Unlimited",
            enterprise: "Unlimited"
        },
        {
            feature:"Maximum concurrent video generations",
            free: "1",
            starter: "Unlimited",
            growth: "Unlimited",
            pro: "Unlimited",
            enterprise: "Unlimited"
        },
         {
            feature: "Sharing & cloud storage",
            free: true,
            starter: true,
            growth: true,
            pro: true,
            enterprise: true
        },
         {
            feature: "API integration",
            free: false,
            starter: false,
            growth: false,
            pro: false,
            enterprise: true
        },
        {
            feature: "Live onboarding session",
            free: false,
            starter: false,
            growth: true,
            pro: true,
            enterprise: true
        },
         {
            feature: "Managed design services",
            free: false,
            starter: false,
            growth: false,
            pro: true,
            enterprise: true
        },
           {
            feature: "Dedicated support",
            starter: false,
            growth: false,
            pro: true,
            enterprise: true
        },   
        {
            feature: "Account manager",
            free: false,
            starter: false,
            growth: false,
            pro: true,
            enterprise: true
        },
        {
            feature: "Photorealistic 3D models",
            free: false,
            starter: false,
            growth: false,
            pro: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "AR view in room",
            free: false,
            starter: false,
            growth: false,
            pro: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "Product silo images",
            free: false,
            starter: false,
            growth: false,
            pro: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "3D CGI images",
            free: false,
            starter: false,
            growth: false,
            pro: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "3D Custom scenes",
            free: false,
            starter: false,
            growth: false,
            pro: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "3D configurator",
            free: false,
            starter: false,
            growth: false,
            pro: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "Project management",
            free: false,
            starter: false,
            growth: false,
            pro: false,
            enterprise: true,
            is3DFeature: true
        },
    ];


    const bundles = [
        {
            for: "For anyone",
            name: "Free Trial",
            type: "bundle",
            package_type: "free_trial_ai",
            desc: "See what's possible",
            price: "$0",
            price_monthly: "$0",
            price_time: "",
            price_details: "14-day limited trial",
            price_details_monthly: "14-day limited trial",
            discount_statement: "",
            discount_statement_monthly: "",
            switch_to_subscription: "",
            button_text: "Start your free trial",
            credits: "",
            credits_monthly: "",
            min_quantity: 0,
            credit_value: 0,
        },
        {
            for: "For self-serve users",
            name: "Basic Bundle",
            type: "bundle",
            package_type: "basic_ai",
            desc: "Flexible & easy-to-manage",
            price: "$49",
            price_monthly: "$49",
            price_time: "/product",
            price_details: "5 image minimum",
            price_details_monthly: "5 image minimum",
            discount_statement: "",
            discount_statement_monthly: "",
            switch_to_subscription: "Switch to subscription – save up to 27%",
            button_text: "Buy ",
            credits: "All 20 credits available at once. 4 credits per product. Unlimited image generations.",
            credits_monthly: "All 20 credits available at once. 4 credits per product. Unlimited image generations.",
            min_quantity: 5,
            credit_value: 4,
        },
        {
            for: "For low-touch sellers",
            name: "Studio Bundle",
            type: "bundle",
            package_type: "studio_bundle",
            desc: "Designer guided & managed",
            price: "$110",
            price_monthly: "$110",
            price_time: "/product",
            price_details: "45 image minimum",
            price_details_monthly: "45 image minimum",
            discount_statement: "",
            discount_statement_monthly: "",
            switch_to_subscription: "",
            button_text: "Buy",
            credits: "12 credits per product. All 540 credits available at once.",
            credits_monthly: "12 credits per product. 540 credits available at once.",
            min_quantity: 45,
            credit_value: 12,
        },
        {
            for: "For large teams",
            name: "Enterprise",
            type: "bundle",
            package_type: "enterprise",
            desc: "Custom plan with 3D/AR",
            credits: "Contact us",
            price: "Let's talk",
            price_monthly: "Let's talk",
            price_details: "Schedule a time to discuss",
            price_details_monthly: "Schedule a time to discuss",
            discount_statement: "",
            discount_statement_monthly: "",
            switch_to_subscription: "",
            button_text: "Schedule a demo",
            credits: <a href="/contact-us" className='manrope f-12 w-500 lh-20 blue'>Contact us</a>,
            credits_monthly: <a href="/contact-us" className='manrope f-12 w-500 lh-20 blue'>Contact us</a>,
            min_quantity: 0,
            credit_value: 0,
        }
    ];

    const basic_features_bundles = [
        {
            feature: "Product uploads",
            free: "3",
            starter: "Unlimited",
            growth: "Unlimited",
            enterprise: "Custom"
        },
        {
            feature: "Image generations",
            free: "3 per product",
            starter: "Unlimited",
            growth: "Unlimited",
            enterprise: "Unlimited"
        },
        {
            feature: "Final 2K or 4K images (no watermark)",
            free: "4 per product",
            starter: "4 per product",
            growth: "4 per product",
            enterprise: "4 per product"
        },
        {
            feature: "Video generation from final image (720p)",
            free: "2 credits each",
            starter: "2 credits each",
            growth: "2 credits each",
            enterprise: "2 credits each"
        },
        {
            feature: "Multiple aspect ratios",
            free: "Square, Landscape, Portrait",
            starter: "Square, Landscape, Portrait",
            growth: "Square, Landscape, Portrait",
            enterprise: "Square, Landscape, Portrait"
        },
        {
            feature: "User licenses",
            free: "1",
            starter: "2",
            growth: "2",
            enterprise: "Unlimited"
        },
    ];

    const plus_features_bundles = [
        {
            feature: "Editing",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Resize product",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Magic erase",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Replace items",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Color correction",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Texture transfer (small items, logos etc)",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Upload inspiration",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Upload props",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Bulk uploads",
            free: false,
            starter: false,
            growth: true,
            enterprise: true
        },
        {
            feature: "Brand guidelines support",
            tooltip: "All generations and edits based on brand aesthetic, lighting, room types, props and camera angles",
            free: false,
            starter: false,
            growth: true,
            enterprise: true
        },
        {
            feature: "Review Gallery (for quality managemet)",
            free: false,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Team collaboration",
            free: false,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "User Management",
            free: false,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Asset management",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "Maximum concurrent image generations",
            free: "1",
            starter: "3",
            growth: "3",
            enterprise: "Unlimited"
        },
        {
            feature: "Maximum concurrent video generations",
            free: "1",
            starter: "Unlimited",
            growth: "Unlimited",
            enterprise: "Unlimited"
        },
        {
            feature: "Sharing & cloud storage",
            free: true,
            starter: true,
            growth: true,
            enterprise: true
        },
        {
            feature: "API integration",
            free: false,
            starter: false,
            growth: false,
            enterprise: true
        },
        {
            feature: "Live onboarding session",
            free: false,
            starter: false,
            growth: true,
            enterprise: true
        },
        {
            feature: "Managed design services",
            free: false,
            starter: false,
            growth: true,
            enterprise: true
        },
        {
            feature: "Dedicated support",
            free: false,
            starter: false,
            growth: true,
            enterprise: true
        },
        {
            feature: "Account manager",
            free: false,
            starter: false,
            growth: false,
            enterprise: true
        },
        {
            feature: "Photorealistic 3D models",
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "AR view in room",
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "Product silo images",
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "3D CGI images",
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "3D Custom scenes",
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "3D configurator",
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
            is3DFeature: true
        },
        {
            feature: "Project management",
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
            is3DFeature: true
        },
    ];

    const [basicFeatures, setBasicFeatures] = useState(basic_features_bundles);
    const [plusFeatures, setPlusFeatures] = useState(plus_features_bundles);
    const [planType, setPlanType] = useState(bundles);
    const [quantity, setQuantity] = useState({});
    const [credits, setCredits] = useState({
        'basic_ai': 20,
        'studio_bundle': 540,
    });

    useEffect(() => {
        if (selectedPlan == 'subscriptions') {
            setBasicFeatures(basic_features_subscriptions);
            setPlusFeatures(plus_features_subscriptions);
            setPlanType(subscriptions);
        } else {
            setBasicFeatures(basic_features_bundles);
            setPlusFeatures(plus_features_bundles);
            setPlanType(bundles);
        }

    }, [selectedPlan]);

    const renderCheckmark = () => (
        <div className="checkmark">
            <CheckOutlined className="manrope f-12 w-700 white" />
        </div>
    );

    const renderValue = (value) => {
        if (typeof value === 'boolean') {
            return value ? renderCheckmark() : <div className="empty-cell"></div>;
        }
        return <span className="feature-value">{value}</span>;
    };

    const handleQuantityChange = (value, plan, credit_value) => {
        setQuantity((prev) => ({
            ...prev,
            [plan]: value,
        }));

        setCredits((prev) => ({
            ...prev,
            [plan]: (value || 0) * credit_value,
        }));
    };

    return (
        <div className="feature-comparison-table">
            <div className={`table-header ${selectedPlan == 'bundles' ? `bundles` : ''}`}>
                <div className="feature-column"></div>
                {planType.map((plan, index) => (
                    <div key={index} className={`plan-column ${['free_trial_ai'].includes(plan.package_type) ? 'top-left' : ''} ${['basic_ai', 'growth_ai'].includes(plan.package_type) ? 'blue-border' : ''} ${index == planType.length - 1 ? 'top-right' : ''}`} >
                        {['basic_ai', 'growth_ai'].includes(plan.package_type) &&
                            <div className='popular-tag-wrapper'>
                                <div className='popular-tag'>
                                    <img src={'/fire.svg'} />
                                    <span className='manrope f-10 w-700 white lh-20 mleft-4'>POPULAR</span>
                                </div>
                            </div>}
                        <Card className="plan-card" bordered={false}>
                            <div className="card-content">
                                <span className="card-subtitle manrope f-14 w-500 lh-20">{plan.for}</span>
                                <span className="card-title manrope f-20 w-700 lh-32">{plan.name}</span>
                                <div className="card-description manrope f-14 w-500 lh-22 mb-24">
                                    {plan.desc}
                                </div>
                                {selectedPlan == 'subscriptions' ?
                                    <>
                                        <div className={`card-price manrope f-26 w-700 lh-40 a-baseline mt-0 mb-0`}>
                                            <span style={{ letterSpacing: '-1px' }}>{annual ? plan.price : plan.price_monthly}</span>
                                            <div className="manrope f-14 w-400 black-085 lh-22">{plan.price_time}</div>
                                        </div>
                                        <div className={`monthly-period manrope f-14 w-400 lh-22 ${['starter_ai', 'growth_ai', 'pro_ai'].includes(plan.package_type) ? 'mb-8' : 'mb-50'}`}>{annual ? plan.price_details : plan.price_details_monthly}</div>

                                    </> :
                                    <div className={`justify-space-between align-end header-flex-no-wrap pdr-16 ${['enterprise', 'free_trial_ai', 'studio_bundle'].includes(plan.package_type) && plan.type == 'bundle' ? 'mb-58' : ''}`}>
                                        <div>
                                            <span className="card-price manrope f-26 black-085 w-700 a-baseline">
                                                {plan.price}
                                                <span className="annual-period manrope f-14 w-400 black-085 lh-22">{plan.price_time}</span>
                                            </span>
                                            <span className={`monthly-period manrope f-14 w-400 lh-22 mb-0`}>{plan.price_details}</span>
                                        </div>
                                        {!['enterprise', 'free_trial_ai'].includes(plan.package_type) && plan.type == 'bundle' &&
                                            <div className="justify-in-end">
                                                <div className="manrope f-10 lh-22 black-085 w-500 mr-8">QTY</div>
                                                <InputNumber
                                                    onKeyDown={(e) => {
                                                        if ((["-", ".", " "].includes(e.key)) || (/[a-zA-Z\s]/.test(e.key) && !allowedKeys.includes(e.key))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    min={plan.min_quantity}
                                                    value={quantity[plan.package_type] || plan.min_quantity}
                                                    onChange={(value) => handleQuantityChange(value, plan.package_type, plan.credit_value)}
                                                    className="manrope f-14 lh-22 black-085 w-400 width-64" />
                                            </div>}
                                    </div>}

                                {plan.discount_statement != "" ? (
                                    <div className="discount-wrapper">
                                        <TagFilled className="discount-icon" />
                                        <span className="discount manrope f-12 w-500">{annual ? plan.discount_statement : plan.discount_statement_monthly}</span>
                                    </div>
                                ) : (
                                    <div className="discount-spacer"></div>
                                )}
                                {plan.switch_to_subscription != "" ? (
                                    <div className="switch-to-subscription-wrapper mb-24 mt-8">
                                        <a onClick={() => setSelectedPlan("subscriptions")}
                                            className="manrope lh-20 w-500 f-12 w-500 blue">{plan.switch_to_subscription}</a>
                                    </div>
                                ) : ''}
                                <div className="pdr-16">
                                    <Button
                                        className={`${['free_trial_ai'].includes(plan.package_type) ? 'free-trial-button' : ''} card-button manrope f-16 w-600 lh-24`}
                                        block
                                        onClick={() => {
                                            const urlParams = new URLSearchParams(window.location.search);
                                            let utmSource = urlParams.get('utm_source');
                                            const email = urlParams.get('email') || "";
                                            const ph = posthog.init(ENVIRONMENT.REGISTER_POSTHOG_API_KEY, {
                                                api_host: ENVIRONMENT.POSTHOG_API_HOST,
                                                autocapture: false,
                                                name: "register",
                                                capture_pageview: false,
                                                disable_session_recording: true,
                                                loaded: (ph) => {
                                                    if (utmSource) {
                                                        ph.register({ utm_source: utmSource });
                                                    }
                                                },
                                            });
                                            if (ph) {
                                                let eventData = {
                                                    plan_name: plan.name,
                                                    plan_type: plan.type,
                                                    package_type: plan.package_type,
                                                    billing_cycle: annual ? 'annual' : 'monthly',
                                                    price: annual ? plan.price : plan.price_monthly,
                                                    location: window.location.pathname,
                                                    utm_source: utmSource,
                                                    email: email,
                                                    button: plan.name + '_' + (annual ? 'annual' : 'monthly')
                                                };

                                                ph.capture('plan_selection_click', eventData);

                                                if (['enterprise'].includes(plan.package_type)) {
                                                    eventData = {
                                                        ...eventData,
                                                        button: 'schedule_a_demo'
                                                    }
                                                    ph.capture('schedule_demo_click', eventData);
                                                } else if (['free_trial_ai'].includes(plan.package_type)) {
                                                    eventData = {
                                                        ...eventData,
                                                        button: 'get_started_for_free'
                                                    }
                                                    ph.capture('free_trial_click', eventData);
                                                }
                                            }

                                            if (['enterprise'].includes(plan.package_type))
                                                window.open(`${ENVIRONMENT.CALENDLY_LINK}`, "_blank")
                                            else if (['free_trial_ai'].includes(plan.package_type))
                                                window.open(`${ENVIRONMENT.FREE_TRIAL_LINK}`, "_blank")
                                            else
                                                window.open(`${ENVIRONMENT.PORTAL_BASE_URL}/register?redirectURL=${encodeURIComponent(`/select-subscription?plan=${annual && plan.type === 'subscription' ? 'annual_' + plan.package_type : plan.package_type}&plan_type=${plan.type}${plan.type === 'bundle' ? `&quantity=${quantity[plan.package_type] || plan.min_quantity}` : ''}&utm_source=${utm_source}`)}`, "_blank")
                                        }}>
                                        {plan.button_text} {credits[plan.package_type] ? ` ${credits[plan.package_type]} credits` : ''}
                                    </Button>
                                </div>

                                <div className="justify-in-center mt-8 pdr-16">
                                    {plan.name != 'Free Trial' && (
                                        plan.credits != "" && plan.type == 'subscription' ?
                                        <div className="manrope f-12 lh-20 w-400 black-085 text-center">{annual ? plan.credits : plan.credits_monthly}</div>
                                        : plan.package_type !== 'enterprise' ? <div className="manrope f-12 lh-20 w-400 black-085 text-center">{`All ${plan.credit_value * (quantity[plan.package_type] || plan.min_quantity)} credits available at once. ${plan.credit_value} credits per product. Unlimited image generations.`}</div> : plan.credits
                                    )}
                                </div>
                            </div>
                        </Card>
                    </div>
                ))}
            </div>

            <div className="table-body">
                {basicFeatures.map((row, index) => (
                    <div key={index} className={`table-row ${selectedPlan == 'bundles' ? `bundles` : ''} ${index == 0 ? 'top-left' : ''}`}>
                        <div className="manrope f-14 lh-22 w-700 black-085 text-left feature-name">
                            {row.feature}
                            {row.tooltip && (
                                <Tooltip title={row.tooltip} placement="top">
                                    <InfoCircleOutlined className="feature-info-icon" style={{ marginLeft: '6px' }} />
                                </Tooltip>
                            )}
                        </div>
                        <div className="manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value">{renderValue(row.free)}</div>
                        <div className={`manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value ${selectedPlan == 'bundles' ? 'blue-border' : ''}`}>
                            {row.feature === "4K images" && selectedPlan === 'bundles' ? renderValue(quantity['basic_ai'] || 5) : renderValue(row.starter)}
                        </div>
                        <div className={`manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value ${selectedPlan == 'subscriptions' ? 'blue-border' : ''}`}>
                            {row.feature === "4K images" && selectedPlan === 'bundles' ? renderValue(quantity['studio_bundle'] || 45) : renderValue(row.growth)}
                        </div>
                        {selectedPlan == 'subscriptions' && <div className="manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value">{renderValue(row.pro)}</div>}
                        <div className="manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value">{renderValue(row.enterprise)}</div>
                    </div>
                ))}
            </div>

            <div className="separator-bar">
                <div><img src={'/ai_creator/ai-star-white.svg'} /></div>
                <div className="manrope f-14 lh-22 w-700 white ml-8">Plus, these advanced editing tools and features</div>
            </div>

            <div className="table-body advanced-features">
                {plusFeatures.filter(row => !row.is3DFeature).map((row, index, arr) => {
                    const threeDFeatures = plusFeatures.filter(r => r.is3DFeature);
                    const isLastNon3D = index === arr.length - 1 && threeDFeatures.length === 0;
                    return (
                        <div key={index} className={`table-row ${selectedPlan == 'bundles' ? `bundles` : ''} ${isLastNon3D ? 'border-bottom-radius' : ''}`}>
                            <div className="manrope f-14 lh-22 w-700 black-085 text-left feature-name">
                                {row.feature}
                                {row.tooltip && (
                                    <Tooltip title={row.tooltip} placement="top">
                                        <InfoCircleOutlined className="feature-info-icon" style={{ marginLeft: '6px' }} />
                                    </Tooltip>
                                )}
                            </div>
                            <div className={`manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value`}>{renderValue(row.free)}</div>
                            <div className={`manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value ${selectedPlan == 'bundles' ? 'blue-border' : ''} ${selectedPlan == 'bundles' && isLastNon3D ? 'blue-border-bottom' : ''}`}>{renderValue(row.starter)}</div>
                            <div className={`manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value ${selectedPlan == 'subscriptions' ? 'blue-border' : ''} ${selectedPlan == 'subscriptions' && isLastNon3D ? 'blue-border-bottom' : ''}`}>{renderValue(row.growth)}</div>
                            {selectedPlan == 'subscriptions' && <div className="manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value">{renderValue(row.pro)}</div>}
                            <div className="manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value">{renderValue(row.enterprise)}</div>
                        </div>
                    );
                })}
            </div>

            {plusFeatures.filter(row => row.is3DFeature).length > 0 && (
                <>
                    <div className="separator-bar three-d-separator">
                        <div><img src={'/ai_creator/ai-star-white.svg'} /></div>
                        <div className="manrope f-14 lh-22 w-700 white ml-8">3D services</div>
                    </div>

                    <div className="table-body three-d-features">
                        {plusFeatures.filter(row => row.is3DFeature).map((row, index, arr) => (
                            <div key={index} className={`table-row ${selectedPlan == 'bundles' ? `bundles` : ''} ${index == arr.length - 1 ? 'border-bottom-radius' : ''}`}>
                                <div className="manrope f-14 lh-22 w-700 black-085 text-left feature-name">
                                    {row.feature}
                                    {row.tooltip && (
                                        <Tooltip title={row.tooltip} placement="top">
                                            <span style={{ display: 'inline', marginLeft: '6px' }}>
                                                <InfoCircleOutlined className="feature-info-icon" />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                <div className={`manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value`}>{renderValue(row.free)}</div>
                                <div className={`manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value ${selectedPlan == 'bundles' ? 'blue-border' : ''} ${selectedPlan == 'bundles' && index == arr.length - 1 ? 'blue-border-bottom' : ''}`}>{renderValue(row.starter)}</div>
                                <div className={`manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value ${selectedPlan == 'subscriptions' ? 'blue-border' : ''} ${selectedPlan == 'subscriptions' && index == arr.length - 1 ? 'blue-border-bottom' : ''}`}>{renderValue(row.growth)}</div>
                                {selectedPlan == 'subscriptions' && <div className="manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value">{renderValue(row.pro)}</div>}
                                <div className="manrope f-14 lh-22 w-500 black-085 text-center justify-in-center plan-value">{renderValue(row.enterprise)}</div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default FeatureComparisonTable;
